'use client';

/**
 * 감사 로그 (API Audit Log) 관련 커스텀 훅
 * - 목록 조회 (검색): useAuditLogs
 * - 상세 조회: useAuditLogDetail
 * - 통계 조회: useAuditLogStats
 */

import { useQuery } from '@tanstack/react-query';
import {
  auditLogsApi,
  type AuditLogSearchRequest,
  type AuditLogStatsParams,
} from '@/services/api';

/**
 * 감사 로그 목록 조회 훅
 * - placeholderData 로 페이지 전환 시 깜빡임 최소화
 * - `refetchKey` 가 바뀌면 동일 조건이어도 강제 재조회.
 *   CS 업무 특성상 "방금 발생한 로그"를 동일 필터로 다시 확인해야 하는 경우가 많아,
 *   검색 버튼을 누를 때마다 무조건 새로 fetch 되도록 호출측에서 nonce 를 증가시켜 전달.
 * - staleTime=0 으로 두어도 같은 queryKey 면 React Query 가 in-flight/캐시를 재사용하므로,
 *   "동일 조건 재검색" 요구를 보장하려면 refetchKey 변경이 필수.
 */
export function useAuditLogs(
  body: AuditLogSearchRequest,
  refetchKey: number = 0,
) {
  return useQuery({
    queryKey: ['audit-logs', body, refetchKey],
    queryFn: () => auditLogsApi.search(body),
    placeholderData: prev => prev,
    // 동일 조건 재검색을 위해 stale 즉시 처리 + GC 는 짧게.
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * 감사 로그 상세 조회 훅
 * - logSeq 가 없으면 비활성
 */
export function useAuditLogDetail(logSeq: number | null) {
  return useQuery({
    queryKey: ['audit-logs', 'detail', logSeq],
    queryFn: () => auditLogsApi.getDetail(logSeq as number),
    enabled: logSeq != null,
    staleTime: 60 * 1000,
  });
}

/**
 * 감사 로그 통계 조회 훅 (대시보드 위젯용)
 * - `refetchKey` 가 바뀌면 동일 기간이어도 강제 재조회.
 */
export function useAuditLogStats(
  params: AuditLogStatsParams = {},
  refetchKey: number = 0,
) {
  return useQuery({
    queryKey: ['audit-logs', 'stats', params, refetchKey],
    queryFn: () => auditLogsApi.getStats(params),
    staleTime: 0,
    gcTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}
